import { BATTLEFIELD_SIZE } from '../shared/config.js';
import { PLAYER_COLOR_CSS, playerColor, rgbCss } from '../shared/colors.js';
import type { GameSnapshot } from '../shared/protocol.js';
import { trickshotLabel, type TrickshotTier } from '../shared/trickshot.js';
import { TrailSystem } from './trails.js';

export interface ViewRect {
  ox: number;
  oy: number;
  size: number;
  battlefield: number;
}

export function computeView(
  canvasW: number,
  canvasH: number,
  battlefield = BATTLEFIELD_SIZE,
  padTop = 0,
  padBottom = 0,
): ViewRect {
  const usableH = Math.max(1, canvasH - padTop - padBottom);
  const size = Math.min(canvasW, usableH);
  const ox = (canvasW - size) / 2;
  const oy = padTop + (usableH - size) / 2;
  return { ox, oy, size, battlefield };
}

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  born: number;
  life: number;
}

interface DeathMark {
  x: number;
  y: number;
  born: number;
  color: string;
}

interface TrickshotBanner {
  x: number;
  y: number;
  born: number;
  label: string;
}

const DEATH_MARK_FADE_MS = 60_000;
const TRICKSHOT_BANNER_MS = 2200;

/**
 * Game-layer renderer. Space background (gradient + stars) is drawn by SpaceBackground
 * behind this transparent canvas so the HUD floats on the same sky.
 */
export class Renderer {
  readonly trails = new TrailSystem();
  private sparks: Spark[] = [];
  private deathMarks: DeathMark[] = [];
  private trickshotBanners: TrickshotBanner[] = [];
  private lastMissileIds = new Set<string>();
  private lastDraw = performance.now();
  /** Single-player palette: slot 0 red, others green. */
  private spMode = false;

  constructor(private canvas: HTMLCanvasElement) {}

  setSinglePlayer(on: boolean): void {
    this.spMode = on;
    this.trails.setSinglePlayer(on);
  }

  private entityColor(slot: number): string {
    if (this.spMode) return slot === 0 ? '#ff2a2a' : '#3dff6a';
    return rgbCss(playerColor(slot));
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width * dpr));
    const h = Math.max(1, Math.floor(rect.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  /** HUD chrome height in canvas pixels (mobile overlays); 0 on desktop. */
  private hudPads(): { top: number; bottom: number } {
    const dpr = this.canvas.width / Math.max(1, this.canvas.getBoundingClientRect().width);
    const topEl = document.querySelector('.hud-top-panel');
    const bottomEl = document.querySelector('.hud-bottom-panel');
    const mobile = window.matchMedia('(max-width: 1024px)').matches;
    if (!mobile || !topEl || !bottomEl) return { top: 0, bottom: 0 };
    return {
      top: topEl.getBoundingClientRect().height * dpr,
      bottom: bottomEl.getBoundingClientRect().height * dpr,
    };
  }

  worldToScreen(view: ViewRect, x: number, y: number): { x: number; y: number } {
    const s = view.size / view.battlefield;
    return { x: view.ox + x * s, y: view.oy + y * s };
  }

  syncMissiles(snapshot: GameSnapshot): void {
    const live = new Set<string>();
    for (const m of snapshot.missiles) {
      live.add(m.id);
      const colorSlot = snapshot.players.find((p) => p.id === m.ownerId)?.colorSlot ?? m.slot;
      this.trails.push(m.id, m.x, m.y, colorSlot, m.isDebris);
    }
    for (const id of this.lastMissileIds) {
      if (!live.has(id)) this.trails.end(id);
    }
    this.lastMissileIds = live;
  }

  /**
   * Single-player: follow physics substep trail (no chords to impact).
   * Call before handling end/impact events so the path already reaches the tip.
   */
  private trailCursor = new Map<string, number>();

  syncSpMissileTrails(
    missiles: {
      id: string;
      slot: number;
      trail: { x: number; y: number }[];
      live: boolean;
      isDebris: boolean;
    }[],
  ): void {
    const live = new Set<string>();
    for (const m of missiles) {
      if (m.live) live.add(m.id);
      const prev = this.trailCursor.get(m.id) ?? 0;
      for (let i = prev; i < m.trail.length; i++) {
        const p = m.trail[i];
        this.trails.push(m.id, p.x, p.y, m.slot, m.isDebris);
      }
      this.trailCursor.set(m.id, m.trail.length);
    }
    this.lastMissileIds = live;
  }

  endSpTrail(id: string, x: number, y: number): void {
    this.trails.end(id, x, y);
    this.trailCursor.delete(id);
    this.lastMissileIds.delete(id);
  }

  spawnPlanetImpact(x: number, y: number): void {
    const now = performance.now();
    for (let i = 0; i < 14; i++) {
      const ang = Math.random() * Math.PI * 2;
      const spd = 40 + Math.random() * 120;
      this.sparks.push({
        x,
        y,
        vx: Math.cos(ang) * spd,
        vy: Math.sin(ang) * spd,
        born: now,
        life: 400 + Math.random() * 500,
      });
    }
  }

  addDeathMark(x: number, y: number, colorSlot: number): void {
    this.deathMarks.push({
      x,
      y,
      born: performance.now(),
      color: this.spMode ? (colorSlot === 0 ? '#ff2a2a' : '#3dff6a') : rgbCss(playerColor(colorSlot)),
    });
  }

  addTrickshotBanner(x: number, y: number, tier: TrickshotTier): void {
    const label = trickshotLabel(tier);
    if (!label) return;
    this.trickshotBanners.push({ x, y, born: performance.now(), label });
  }

  clearDeathMarks(): void {
    this.deathMarks = [];
    this.trickshotBanners = [];
  }

  /** Wipe trails, sparks, death marks for a clean board / new round. */
  wipeRoundVisuals(): void {
    this.trails.clear();
    this.sparks = [];
    this.lastMissileIds.clear();
    this.trailCursor.clear();
    this.clearDeathMarks();
  }

  private drawDeathMarks(ctx: CanvasRenderingContext2D, view: ViewRect): void {
    const now = performance.now();
    const next: DeathMark[] = [];
    const fontSize = Math.max(10, view.size * 0.018);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `${fontSize}px "Turret Road", sans-serif`;
    for (const m of this.deathMarks) {
      const age = now - m.born;
      if (age >= DEATH_MARK_FADE_MS) continue;
      const alpha = 1 - age / DEATH_MARK_FADE_MS;
      const c = this.worldToScreen(view, m.x, m.y);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = m.color;
      ctx.fillText('☠', c.x, c.y);
      ctx.globalAlpha = 1;
      next.push(m);
    }
    this.deathMarks = next;
  }

  private drawTrickshotBanners(ctx: CanvasRenderingContext2D, view: ViewRect): void {
    const now = performance.now();
    const next: TrickshotBanner[] = [];
    const fontSize = Math.max(14, view.size * 0.028);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.font = `200 ${fontSize}px "Turret Road", sans-serif`;
    for (const b of this.trickshotBanners) {
      const age = now - b.born;
      if (age >= TRICKSHOT_BANNER_MS) continue;
      const t = age / TRICKSHOT_BANNER_MS;
      const alpha = t < 0.15 ? t / 0.15 : 1 - (t - 0.15) / 0.85;
      const rise = view.size * 0.02 * t;
      const c = this.worldToScreen(view, b.x, b.y);
      ctx.globalAlpha = Math.max(0, alpha);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(b.label, c.x, c.y - rise - view.size * 0.02);
      ctx.globalAlpha = 1;
      next.push(b);
    }
    this.trickshotBanners = next;
  }

  private drawSparks(ctx: CanvasRenderingContext2D, view: ViewRect, dt: number): void {
    const now = performance.now();
    const next: Spark[] = [];
    for (const s of this.sparks) {
      const age = now - s.born;
      if (age > s.life) continue;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.vx *= 0.98;
      s.vy *= 0.98;
      const alpha = 1 - age / s.life;
      const c = this.worldToScreen(view, s.x, s.y);
      const r = Math.max(0.8, 2.2 * alpha);
      ctx.beginPath();
      ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${alpha})`;
      ctx.fill();
      next.push(s);
    }
    this.sparks = next;
  }

  draw(snapshot: GameSnapshot | null, aim?: { x: number; y: number; angleInternal: number }): void {
    this.resize();
    const ctx = this.canvas.getContext('2d')!;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const flash = snapshot?.killFlash ?? 0;
    const now = performance.now();
    const dt = Math.min(0.05, (now - this.lastDraw) / 1000);
    this.lastDraw = now;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (flash > 0.02) {
      ctx.fillStyle = `rgba(255,255,255,${flash * 0.35})`;
      ctx.fillRect(0, 0, w, h);
    }

    const bf = snapshot?.battlefieldSize ?? BATTLEFIELD_SIZE;
    const pads = this.hudPads();
    const view = computeView(w, h, bf, pads.top, pads.bottom);
    const toScreen = (x: number, y: number) => this.worldToScreen(view, x, y);

    // Trails / FX first — drawn across the full canvas, including outside the field square
    this.trails.draw(ctx, toScreen);
    this.drawSparks(ctx, view, dt);
    this.drawDeathMarks(ctx, view);
    this.drawTrickshotBanners(ctx, view);

    if (!snapshot) return;

    for (const p of snapshot.planets) {
      const c = toScreen(p.x, p.y);
      const r = (p.radius / bf) * view.size;
      ctx.beginPath();
      ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
      ctx.fillStyle = '#12182a';
      ctx.fill();
      ctx.strokeStyle = 'rgba(140,200,255,0.7)';
      ctx.lineWidth = 1.6;
      ctx.stroke();
    }

    for (const m of snapshot.missiles) {
      if (m.isDebris) continue;
      const c = toScreen(m.x, m.y);
      const owner = snapshot.players.find((p) => p.id === m.ownerId);
      const color = this.entityColor(owner?.colorSlot ?? m.slot);
      ctx.beginPath();
      ctx.arc(c.x, c.y, 2.3, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();

      // Flight time label from 1s onward (ageTicks @ 60 Hz)
      if (m.ageTicks >= 60) {
        const ms = Math.round((m.ageTicks / 60) * 1000);
        ctx.fillStyle = color;
        ctx.font = `200 ${Math.max(9, view.size * 0.014)}px "Turret Road", sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${ms}`, c.x + 8, c.y);
      }
    }

    for (const pl of snapshot.players) {
      const c = toScreen(pl.x, pl.y);
      const color = this.entityColor(pl.colorSlot);
      const r = (4 / bf) * view.size;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
      ctx.moveTo(c.x - r * 1.6, c.y);
      ctx.lineTo(c.x + r * 1.6, c.y);
      ctx.moveTo(c.x, c.y - r * 1.6);
      ctx.lineTo(c.x, c.y + r * 1.6);
      ctx.stroke();

      ctx.fillStyle = color;
      ctx.font = `200 ${Math.max(8, view.size * 0.012)}px "Turret Road", sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(pl.name, c.x, c.y - r - 18);
    }

    if (aim) {
      const c = toScreen(aim.x, aim.y);
      const rad = (aim.angleInternal / 180) * Math.PI;
      const len = view.size * 0.04;
      ctx.strokeStyle = 'rgba(255,255,255,0.65)';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(c.x, c.y);
      ctx.lineTo(c.x + Math.cos(rad) * len, c.y + -Math.sin(rad) * len);
      ctx.stroke();
    }
  }
}
